import { useState, useEffect, useCallback } from "react";
import { useOnlineStatus } from "../../hooks/useSync";
import { apiListarJornadas, cachearJornadas, leerJornadasCache, leerJornadasPendientes } from "./jornadasBac";

// Según el motor de la base, la columna JSON puede llegar como texto
// ('["Ana","Beto"]') en vez de arreglo — se deja siempre como arreglo.
const aLista = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};
const normalizarJornada = (j) => ({ ...j, entrevistadores: aLista(j.entrevistadores).map((x) => String(x).trim()).filter(Boolean) });

// Jornadas de la comunidad elegida + la que está ABIERTA (la activa).
// Funciona sin señal: usa la última lista guardada y las jornadas que se
// abrieron en el dispositivo y aún no suben.
export default function useJornadasBac(territorio, comunidad) {
  const online = useOnlineStatus();
  const [jornadas, setJornadas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [desdeCache, setDesdeCache] = useState(false);
  // Clave (territorio|comunidad) de la última carga terminada — para saber
  // que la lista ya corresponde a la comunidad elegida.
  const [listoPara, setListoPara] = useState("");

  const cargar = useCallback(async () => {
    if (!territorio || !comunidad) { setJornadas([]); setListoPara(""); return; }
    setCargando(true);
    let base = [];
    let cache = false;
    if (online) {
      try {
        const res = await apiListarJornadas({ territorio, comunidad });
        base = res.data || [];
        await cachearJornadas(territorio, comunidad, base);
      } catch (e) {
        console.warn("⚠️ Jornadas desde caché:", e.message);
        base = await leerJornadasCache(territorio, comunidad);
        cache = true;
      }
    } else {
      base = await leerJornadasCache(territorio, comunidad);
      cache = true;
    }
    base = base.map(normalizarJornada);
    const pendientes = await leerJornadasPendientes(territorio, comunidad);
    const codigos = new Set(base.map((j) => j.codigo));
    setJornadas([...pendientes.filter((p) => !codigos.has(p.codigo)), ...base]);
    setDesdeCache(cache);
    setCargando(false);
    setListoPara(`${territorio}|${comunidad}`);
  }, [territorio, comunidad, online]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const f = () => cargar();
    window.addEventListener("jornadas-cambiaron", f);
    window.addEventListener("sync-completado", f);
    return () => {
      window.removeEventListener("jornadas-cambiaron", f);
      window.removeEventListener("sync-completado", f);
    };
  }, [cargar]);

  // La activa: la abierta del servidor; si no hay, la abierta sin señal.
  const activa = jornadas.find((j) => j.estado === "abierta" && !j._pendiente)
    || jornadas.find((j) => j.estado === "abierta" && j._pendiente && !j._error)
    || null;

  const listo = listoPara === `${territorio}|${comunidad}`;
  return { jornadas, activa, cargando, desdeCache, listo, recargar: cargar };
}
